import { Divider, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import {
  STANDARD_LAYOUT_PADDING_X,
  STANDARD_LAYOUT_PADDING_Y,
  Text,
  api,
  colors,
  getQueryProps,
  isDefined,
  isTruthy,
  useLoggedInAuthContext,
} from 'common';

import { FlowTemplatePreviewPanelGroup } from './Panel/Group';
import { FlowTemplatePreviewPanelTag } from './Panel/Tag';
import { Time } from '../../../../../components/Time';
import { UserLabel } from '../../../../../components/Users/Label';
import {
  REVENUE_SHARE_ELIGIBILITY_STATUS_TO_CONTENT,
  RevenueShareEligibilityStatus,
} from '../constants';
import { FLOW_TEMPLATE_VISIBILITY_LABELS, parseTemplateTags } from '../utils';

const TemplateTagCategory = api.FlowDefinitionTemplate.TagCategory;
const TemplateVisibility = api.FlowDefinitionTemplate.TemplateVisibility;

interface FlowTemplatePreviewPanelProps {
  flowTemplate: api.FlowDefinitionTemplate.FlowDefinitionTemplate;
}

const MIN_WIDTH = 200;
const MAX_WIDTH = 300;

export function FlowTemplatePreviewPanel(props: FlowTemplatePreviewPanelProps) {
  const { flowTemplate } = props;

  const { isAdminRole } = useLoggedInAuthContext();

  const { data: organization } = useQuery({
    ...getQueryProps(
      api.client.Organization,
      'findOneById',
      flowTemplate.organizationId ?? 'PLACEHOLDER'
    ),
    enabled: isTruthy(flowTemplate.organizationId),
    retry: 0,
  });

  const tagsByCategory = parseTemplateTags(flowTemplate);

  return (
    <Stack
      gap={4}
      sx={{
        minWidth: MIN_WIDTH,
        maxWidth: MAX_WIDTH,
        height: 'fit-content',
        paddingX: STANDARD_LAYOUT_PADDING_X,
        paddingY: STANDARD_LAYOUT_PADDING_Y * 2,
        backgroundColor: colors.background.default,
        borderRadius: 2,
      }}
    >
      {isDefined(flowTemplate.createdById) && (
        <FlowTemplatePreviewPanelGroup title="Created By">
          <Text variant="body2">
            <UserLabel
              userId={flowTemplate.createdById}
              fallbackOnError={flowTemplate.createdByEmail ?? flowTemplate.createdById}
            />
          </Text>
        </FlowTemplatePreviewPanelGroup>
      )}
      <FlowTemplatePreviewPanelGroup title="Last Update">
        <Text variant="body2">
          <Time when={flowTemplate.updated ?? 'N/A'} format="human-readable-date" />
        </Text>
      </FlowTemplatePreviewPanelGroup>
      <Divider />
      <FlowTemplatePreviewPanelTag
        title="Solution Area"
        tags={tagsByCategory[TemplateTagCategory.SolutionArea]}
      />
      <FlowTemplatePreviewPanelTag
        title="Use Case"
        tags={tagsByCategory[TemplateTagCategory.UseCase]}
      />
      <FlowTemplatePreviewPanelTag
        title="Flow Type"
        tags={tagsByCategory[TemplateTagCategory.FlowType]}
      />
      <FlowTemplatePreviewPanelTag
        title="Compatible EHRs"
        tags={tagsByCategory[TemplateTagCategory.SupportedEhr]}
      />
      <FlowTemplatePreviewPanelGroup title="Access Level">
        <Text variant="body2">
          {flowTemplate.visibility === TemplateVisibility.Organization
            ? (organization?.name ?? 'N/A')
            : FLOW_TEMPLATE_VISIBILITY_LABELS[flowTemplate.visibility]}
        </Text>
      </FlowTemplatePreviewPanelGroup>
      {isAdminRole && (
        <FlowTemplatePreviewPanelGroup title="Revenue Share Eligibility">
          <Text variant="body2">
            {
              REVENUE_SHARE_ELIGIBILITY_STATUS_TO_CONTENT[
                flowTemplate.metadata?.revenueShareEligibilityStatus ??
                  RevenueShareEligibilityStatus.NotEligible
              ]
            }
          </Text>
        </FlowTemplatePreviewPanelGroup>
      )}
    </Stack>
  );
}
